import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Divider, Drawer, Menu, MenuItem, ListItemIcon, ListItemText, InputBase } from '@mui/material'
import { ExpandMore, Logout, ChevronLeft, Settings, FitnessCenter, CalendarMonth, People, Person, Visibility, SportsGymnastics, MonitorHeart, Bedtime, FavoriteBorder, DirectionsRun, Timeline, DashboardCustomize, AutoAwesome, Cable, NotificationsActive, FactCheck, Sync, Description, Today } from '@mui/icons-material'
import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { beoflowColors, typography, spacing, borderRadius } from '../../theme/muiTheme'
import { navigationSpec } from '../../theme/BeoflowNavigationSpec.generated'
import { useAuth } from '@/contexts/AuthContext'
import { useAthlete } from '@/contexts/AthleteContext'
import { useUserProfile } from '@/services/api/settings'
import { openIntegrationsSync } from '@/lib/integrationsSync'

const SIDEBAR_EXPANDED_KEY = 'beoflow_sidebar_groups'

interface SidebarProps {
  open: boolean
  onExpandedChange?: (expanded: boolean) => void
  onClose?: () => void
  variant?: 'permanent' | 'temporary'
  showBackButton?: boolean
}

interface NavItem {
  label: string
  path: string
  icon?: React.ReactNode
  glass?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

// Rail widths come from the generated BeoflowNavigationSpec (Drafted source of truth) — never hardcode.
const COLLAPSED_WIDTH = `${navigationSpec.width.railCollapsed}px`
const EXPANDED_WIDTH = `${navigationSpec.width.railExpanded}px`
const LANDSCAPE_PHONE_QUERY = '@media (orientation: landscape) and (max-height: 600px)'

function NavItemButton({ label, selected, onClick, py = spacing.sm, icon, collapsed, glass }: {
  label: string
  selected: boolean
  onClick: () => void
  py?: string
  icon?: React.ReactNode
  collapsed?: boolean
  glass?: boolean
}) {
  const glassStyles = glass ? {
    mx: collapsed ? spacing.sm : spacing.md,
    px: collapsed ? 0 : spacing.md,
    background: 'rgba(248, 246, 244, 0.12)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(248, 246, 244, 0.25)',
    borderRadius: '14px',
    boxShadow: selected
      ? '0 0 12px rgba(0, 0, 0, 0.25)'
      : '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition: 'box-shadow 0.2s ease, background 0.2s ease',
    '&:hover': {
      background: 'rgba(248, 246, 244, 0.18)',
      boxShadow: '0 0 12px rgba(0, 0, 0, 0.25)',
    },
  } : {}

  const glassColor = undefined

  return (
    <Box
      onClick={onClick}
      sx={{
        px: collapsed ? 0 : spacing.lg,
        py,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: spacing.sm,
        '&:hover': {
          backgroundColor: selected ? 'transparent' : 'rgba(0,0,0,0.1)',
        },
        ...glassStyles,
      }}
    >
      {icon && (
        <Box sx={{
          color: glassColor ?? (selected ? beoflowColors.beoWhite : beoflowColors.beoBlack),
          display: 'flex',
          alignItems: 'center',
          '& .MuiSvgIcon-root': { fontSize: '22px' },
        }}>
          {icon}
        </Box>
      )}
      {!collapsed && (
        <Typography
          sx={{
            fontFamily: typography.fontFamilyMonospace,
            fontSize: typography.fontSizeSm,
            color: glassColor ?? (selected ? beoflowColors.beoWhite : beoflowColors.beoBlack),
            fontWeight: glass ? typography.fontWeightSemibold : (selected ? typography.fontWeightSemibold : typography.fontWeightNormal),
            letterSpacing: `${navigationSpec.label.tracking}px`,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

function loadExpandedGroups(): Set<string> {
  try {
    const stored = localStorage.getItem(SIDEBAR_EXPANDED_KEY)
    if (stored) return new Set(JSON.parse(stored) as string[])
  } catch { /* ignore */ }
  return new Set()
}

export default function Sidebar({ open, onExpandedChange, onClose, variant = 'permanent', showBackButton = false }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuth()
  const [isExpanded, setIsExpanded] = useState(variant === 'temporary')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(loadExpandedGroups)
  const [athleteAnchorEl, setAthleteAnchorEl] = useState<null | HTMLElement>(null)
  const [athleteSearch, setAthleteSearch] = useState('')

  const toggleGroup = useCallback((label: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify([...next]))
      return next
    })
  }, [])

  const safeGoBack = useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate('/calendar', { replace: true })
  }, [navigate])

  const athleteCtx = useAthlete()
  const { data: profile } = useUserProfile()
  const isCoachAccount = profile?.account_type === 'professional-monitor' || athleteCtx.isCoach
  // Narrower than isCoachAccount on purpose: App.tsx's RequireCoachAccount gates
  // /coach/alerts and /coach/suggestions on the ACCOUNT TYPE alone, so listing
  // them for someone who is only a monitor of one athlete would be a nav entry
  // that bounces straight back to the calendar.
  const isProfessionalCoach = profile?.account_type === 'professional-monitor'
  const canSelectAthletes = athleteCtx.athletes.length > 0

  const navGroups: NavGroup[] = []

  // Athlete-selectable pages (data changes with selected athlete)
  const athleteItems: NavItem[] = [
    { label: 'CALENDAR', path: '/calendar', icon: <CalendarMonth /> },
    { label: 'PERIODIZATION', path: '/periodization', icon: <Timeline /> },
    { label: 'SLEEP', path: '/dashboard/sleep', icon: <Bedtime /> },
    { label: 'RECOVERY', path: '/dashboard/recovery', icon: <FavoriteBorder /> },
    { label: 'EXERCISE', path: '/dashboard/exercise', icon: <DirectionsRun /> },
    { label: 'TRAINING ZONES', path: '/exercise-zones', icon: <MonitorHeart /> },
  ]

  // Coach's own tools (always show coach's own data)
  const toolItems: NavItem[] = [
    { label: 'TEAM', path: '/team', icon: <People /> },
    // What needs you, then who needs you, then what to change about it: the
    // order the onboarding stages introduce them in. TODAY leads because it is
    // the aggregation of the next three, and it shares their account-type gate.
    ...(isProfessionalCoach ? [
      { label: 'TODAY', path: '/coach/today', icon: <Today /> },
      { label: 'ALERTS', path: '/coach/alerts', icon: <NotificationsActive /> },
      { label: 'SUGGESTIONS', path: '/coach/suggestions', icon: <FactCheck /> },
    ] : []),
    { label: 'LENS', path: '/views', icon: <DashboardCustomize /> },
    { label: 'PROGRAMS', path: '/programs', icon: <CalendarMonth /> },
    { label: 'WORKOUTS', path: '/workouts', icon: <FitnessCenter /> },
    { label: 'EXERCISES', path: '/exercises', icon: <SportsGymnastics /> },
    { label: 'DOCS', path: '/docs', icon: <Description /> },
  ]

  const athleteDataItems: NavItem[] = [
    { label: 'CALENDAR', path: '/calendar', icon: <CalendarMonth /> },
    { label: 'PERIODIZATION', path: '/periodization', icon: <Timeline /> },
    { label: 'SLEEP', path: '/dashboard/sleep', icon: <Bedtime /> },
    { label: 'RECOVERY', path: '/dashboard/recovery', icon: <FavoriteBorder /> },
    { label: 'EXERCISE', path: '/dashboard/exercise', icon: <DirectionsRun /> },
    { label: 'TRAINING ZONES', path: '/exercise-zones', icon: <MonitorHeart /> },
  ]
  const athleteToolItems: NavItem[] = [
    { label: 'WORKOUTS', path: '/workouts', icon: <FitnessCenter /> },
    { label: 'EXERCISES', path: '/exercises', icon: <SportsGymnastics /> },
    { label: 'DOCS', path: '/docs', icon: <Description /> },
  ]

  const isSelected = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  const handleMouseEnter = () => {
    if (variant === 'permanent') {
      setIsExpanded(true)
      onExpandedChange?.(true)
    }
  }

  const handleMouseLeave = () => {
    if (variant === 'permanent') {
      setIsExpanded(false)
      onExpandedChange?.(false)
    }
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    if (variant === 'temporary') {
      onClose?.()
    }
  }

  const handleLogout = () => {
    logout()
    if (variant === 'temporary') {
      onClose?.()
    }
  }

  const ownUserName = user?.first_name || user?.last_name
    ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
    : 'Anonymous User'
  const viewedUserName = athleteCtx.selectedProfileName || ownUserName
  const selectedAthlete = useMemo(
    () => athleteCtx.athletes.find((athlete) => athlete.profile_id === athleteCtx.selectedProfileId) ?? null,
    [athleteCtx.athletes, athleteCtx.selectedProfileId],
  )
  const groupedAthletes = useMemo(() => {
    const query = athleteSearch.trim().toLowerCase()
    const groups = new Map<string, typeof athleteCtx.athletes>()
    for (const athlete of athleteCtx.athletes) {
      if (query && !athlete.name.toLowerCase().includes(query)) continue
      const groupName = athlete.group_name || 'Ungrouped'
      groups.set(groupName, [...(groups.get(groupName) ?? []), athlete])
    }
    return [...groups.entries()]
  }, [athleteCtx.athletes, athleteSearch])

  if (!open && variant === 'permanent') return null

  const sidebarContent = (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: isExpanded ? EXPANDED_WIDTH : (variant === 'temporary' ? EXPANDED_WIDTH : COLLAPSED_WIDTH),
        height: '100dvh',
        maxHeight: '100dvh',
        backgroundColor: beoflowColors.peakOrange,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        [LANDSCAPE_PHONE_QUERY]: {
          overflowY: isExpanded ? 'auto' : 'hidden',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        },
        transition: variant === 'permanent' ? 'width 0.3s ease' : 'none',
      }}
    >
      {/* Header with Logo */}
      <Box
        sx={{
          p: spacing.lg,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          cursor: 'pointer',
          minHeight: '64px',
          flexShrink: 0,
        }}
        onClick={() => navigate('/')}
      >
        <img
          src={`${import.meta.env.BASE_URL}logomark.svg`}
          alt="Beoflow"
          style={{ width: '48px', height: '48px', flexShrink: 0 }}
        />
      </Box>

      {/* User name */}
      {isExpanded && user && (
        <Box
          onClick={(event) => {
            if (canSelectAthletes) setAthleteAnchorEl(event.currentTarget)
          }}
          sx={{
            px: spacing.lg,
            pt: spacing.sm,
            pb: spacing.sm,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            cursor: canSelectAthletes ? 'pointer' : 'default',
            '&:hover': canSelectAthletes ? { backgroundColor: 'rgba(0,0,0,0.08)' } : undefined,
          }}
        >
          {athleteCtx.selectedProfileId && (
            <Visibility sx={{ fontSize: 16, color: beoflowColors.beoWhite }} />
          )}
          <Typography
            sx={{
              fontFamily: typography.fontFamilyMonospace,
              fontSize: typography.fontSizeSm,
              color: beoflowColors.beoWhite,
              fontWeight: typography.fontWeightBold,
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
            }}
          >
            {viewedUserName}
          </Typography>
        </Box>
      )}
      {!isExpanded && <Box sx={{ height: '40px', flexShrink: 0 }} />}

      {canSelectAthletes && (
        <Menu
          anchorEl={athleteAnchorEl}
          open={Boolean(athleteAnchorEl)}
          onClose={() => setAthleteAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 220,
                maxHeight: 360,
              },
            },
          }}
        >
          {/* Same entry the navbar's athlete chip offers, directly under the
              name it applies to. */}
          <MenuItem
            onClick={() => {
              void openIntegrationsSync({
                userId: selectedAthlete?.user_id,
                profileId: athleteCtx.selectedProfileId,
              })
              setAthleteAnchorEl(null)
            }}
          >
            <ListItemIcon><Sync fontSize="small" /></ListItemIcon>
            <ListItemText>Sync data</ListItemText>
          </MenuItem>
          <Divider />
          <Box sx={{ px: spacing.md, py: spacing.sm }}>
            <InputBase
              autoFocus
              placeholder="Search athletes"
              value={athleteSearch}
              onChange={(event) => setAthleteSearch(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              sx={{
                width: '100%',
                px: spacing.sm,
                py: '4px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: borderRadius.medium,
                fontFamily: typography.fontFamilyMonospace,
                fontSize: typography.fontSizeSm,
              }}
            />
          </Box>
          <MenuItem
            selected={athleteCtx.selectedProfileId === null}
            onClick={() => {
              athleteCtx.setSelectedProfile(null, null)
              setAthleteSearch('')
              setAthleteAnchorEl(null)
            }}
          >
            <ListItemIcon><Person fontSize="small" /></ListItemIcon>
            <ListItemText>Myself</ListItemText>
          </MenuItem>
          {groupedAthletes.map(([groupName, athletes]) => (
            <Box key={groupName}>
              <Typography variant="monoSmall" sx={{ px: spacing.md, pt: spacing.sm, pb: spacing.xs, color: 'text.secondary' }}>
                {groupName}
              </Typography>
              {athletes.map((athlete) => (
                <MenuItem
                  key={athlete.profile_id}
                  selected={athleteCtx.selectedProfileId === athlete.profile_id}
                  onClick={() => {
                    athleteCtx.setSelectedProfile(athlete.profile_id, athlete.name)
                    setAthleteSearch('')
                    setAthleteAnchorEl(null)
                  }}
                >
                  <ListItemIcon><People fontSize="small" /></ListItemIcon>
                  <ListItemText>{athlete.name}</ListItemText>
                </MenuItem>
              ))}
            </Box>
          ))}
          {!athleteCtx.isLoading && athleteCtx.athletes.length > 0 && groupedAthletes.length === 0 && (
            <MenuItem disabled>
              <ListItemText>No matching athletes</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Chat-mode toggle — matches the chat sidebar's "Go to app" button (radius + spacing) */}
      <Box sx={{ px: spacing.sm, pb: spacing.sm, flexShrink: 0 }}>
        <Box
          component="button"
          type="button"
          onClick={() => handleNavigation('/chat')}
          sx={{
            display: 'flex', alignItems: 'center', gap: spacing.sm, width: '100%',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            px: isExpanded ? spacing.md : 0, py: spacing.sm, cursor: 'pointer',
            borderRadius: borderRadius.medium,
            background: 'transparent',
            border: 'none',
            color: beoflowColors.beoBlack,
            transition: 'background 0.2s ease',
            '&:hover': { background: 'rgba(0, 0, 0, 0.1)' },
          }}
        >
          <AutoAwesome sx={{ fontSize: 18 }} />
          {isExpanded && (
            <Typography variant="monoLabel" sx={{ color: beoflowColors.beoBlack, whiteSpace: 'nowrap' }}>
              Switch to chat mode
            </Typography>
          )}
        </Box>
      </Box>

      {/* Navigation Items */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: isExpanded ? 'auto' : 'hidden',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          [LANDSCAPE_PHONE_QUERY]: {
            flex: 'none',
            minHeight: 'auto',
            overflow: 'visible',
          },
        }}
      >
        {isExpanded ? (
          /* Expanded: Show full accordion groups */
          <>
            {navGroups.map((group) => (
              <Accordion
                key={group.label}
                expanded={expandedGroups.has(group.label)}
                onChange={() => toggleGroup(group.label)}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  border: 'none !important',
                  '&:before': { display: 'none' },
                  margin: '0 !important',
                  borderRadius: '0 !important',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMore
                      sx={{
                        color: beoflowColors.beoBlack,
                        fontSize: '20px',
                      }}
                    />
                  }
                  sx={{
                    backgroundColor: 'transparent',
                    minHeight: '48px',
                    px: spacing.lg,
                    border: 'none !important',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '0',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'transform 0.2s',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(180deg)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamilyMonospace,
                      fontWeight: typography.fontWeightNormal,
                      fontSize: typography.fontSizeSm,
                      color: beoflowColors.beoBlack,
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {group.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: beoflowColors.orangeDark,
                    padding: 0,
                    border: 'none !important',
                  }}
                >
                  {group.items.map((item) => (
                    <NavItemButton
                      key={item.path}
                      label={item.label}
                      selected={isSelected(item.path)}
                      onClick={() => handleNavigation(item.path)}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        ) : (
          /* Collapsed: Show icon placeholders only */
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Placeholder for collapsed state - just show spacing */}
          </Box>
        )}

        {/* Coach segmented nav */}
        {isCoachAccount && (
          <>
            {/* Section: User data (athlete-selectable) */}
            {isExpanded && (
              <Box sx={{ px: spacing.lg, pt: spacing.xs, pb: spacing.xs }}>
                <Typography sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: '9px',
                  fontWeight: typography.fontWeightBold,
                  color: beoflowColors.beoBlack,
                  opacity: 0.5,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}>
                  ATHLETE DATA
                </Typography>
              </Box>
            )}
            {!isExpanded && <Box sx={{ height: '24px', flexShrink: 0 }} />}

            {athleteItems.map((item) => (
              <NavItemButton
                key={item.path}
                label={item.label}
                icon={item.icon}
                selected={isSelected(item.path)}
                onClick={() => handleNavigation(item.path)}
                py={spacing.md}
                collapsed={!isExpanded}
                glass={item.glass}
              />
            ))}

            {/* Divider between sections */}
            {isExpanded ? (
              <Box sx={{ px: spacing.lg, pt: spacing.lg, pb: spacing.xs }}>
                <Typography sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: '9px',
                  fontWeight: typography.fontWeightBold,
                  color: beoflowColors.beoBlack,
                  opacity: 0.5,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}>
                  COACHING TOOLS
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                mx: spacing.md,
                my: spacing.sm,
                borderTop: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.15)',
              }} />
            )}

            {toolItems.map((item) => (
              <NavItemButton
                key={item.path}
                label={item.label}
                icon={item.icon}
                selected={isSelected(item.path)}
                onClick={() => handleNavigation(item.path)}
                py={spacing.md}
                collapsed={!isExpanded}
                glass={item.glass}
              />
            ))}
          </>
        )}

        {!isCoachAccount && (
          <>
            {isExpanded && (
              <Box sx={{ px: spacing.lg, pt: spacing.xs, pb: spacing.xs }}>
                <Typography sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: '9px',
                  fontWeight: typography.fontWeightBold,
                  color: beoflowColors.beoBlack,
                  opacity: 0.5,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}>
                  ATHLETE DATA
                </Typography>
              </Box>
            )}
            {!isExpanded && <Box sx={{ height: '24px', flexShrink: 0 }} />}

            {athleteDataItems.map((item) => (
              <NavItemButton
                key={item.path}
                label={item.label}
                icon={item.icon}
                selected={isSelected(item.path)}
                onClick={() => handleNavigation(item.path)}
                py={spacing.md}
                collapsed={!isExpanded}
                glass={item.glass}
              />
            ))}

            {isExpanded ? (
              <Box sx={{ px: spacing.lg, pt: spacing.lg, pb: spacing.xs }}>
                <Typography sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: '9px',
                  fontWeight: typography.fontWeightBold,
                  color: beoflowColors.beoBlack,
                  opacity: 0.5,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}>
                  COACHING TOOLS
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                mx: spacing.md,
                my: spacing.sm,
                borderTop: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.15)',
              }} />
            )}

            {athleteToolItems.map((item) => (
              <NavItemButton
                key={item.path}
                label={item.label}
                icon={item.icon}
                selected={isSelected(item.path)}
                onClick={() => handleNavigation(item.path)}
                py={spacing.md}
                collapsed={!isExpanded}
                glass={item.glass}
              />
            ))}
          </>
        )}
      </Box>

      {/* Bottom Actions - Only when expanded */}
      <Box sx={{ display: isExpanded ? 'flex' : 'none', flexDirection: 'column', flexShrink: 0 }}>
        {/* Back Button */}
        {showBackButton && (
          <Box
            onClick={() => {
              safeGoBack()
              if (variant === 'temporary') onClose?.()
            }}
            sx={{
              px: spacing.lg,
              py: spacing.md,
              cursor: 'pointer',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.1)',
              },
            }}
          >
            <ChevronLeft
              sx={{
                color: beoflowColors.beoBlack,
                fontSize: '24px',
                flexShrink: 0,
              }}
            />
            {isExpanded && (
              <Typography
                sx={{
                  fontFamily: typography.fontFamilyMonospace,
                  fontSize: typography.fontSizeSm,
                  color: beoflowColors.beoBlack,
                  fontWeight: typography.fontWeightSemibold,
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                  opacity: isExpanded ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                BACK
              </Typography>
            )}
          </Box>
        )}

        <NavItemButton
          label="ACCESS"
          icon={<Visibility />}
          selected={isSelected('/access')}
          onClick={() => handleNavigation('/access')}
          py={spacing.md}
          collapsed={!isExpanded}
        />

        {/* Standalone surface — opens in its own tab, so never "selected". */}
        <NavItemButton
          label="INTEGRATIONS"
          icon={<Cable />}
          selected={false}
          onClick={() => {
            window.open('/integrations', '_blank', 'noopener,noreferrer')
            if (variant === 'temporary') onClose?.()
          }}
          py={spacing.md}
          collapsed={!isExpanded}
        />

        {/* Settings Button */}
        <Box
          onClick={() => handleNavigation('/settings')}
          sx={{
            px: spacing.lg,
            py: spacing.md,
            cursor: 'pointer',
            backgroundColor: isSelected('/settings') ? 'rgba(0,0,0,0.15)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)',
            },
          }}
        >
          <Settings
            sx={{
              color: beoflowColors.beoBlack,
              fontSize: '24px',
              flexShrink: 0,
            }}
          />
          {isExpanded && (
            <Typography
              sx={{
                fontFamily: typography.fontFamilyMonospace,
                fontSize: typography.fontSizeSm,
                color: beoflowColors.beoBlack,
                fontWeight: isSelected('/settings') ? typography.fontWeightSemibold : typography.fontWeightNormal,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                opacity: isExpanded ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              SETTINGS
            </Typography>
          )}
        </Box>

        {/* Logout Button */}
        <Box
          onClick={handleLogout}
          sx={{
            px: spacing.lg,
            py: spacing.lg,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)',
            },
          }}
        >
          <Logout
            sx={{
              color: beoflowColors.beoBlack,
              fontSize: '24px',
              flexShrink: 0,
            }}
          />
          {isExpanded && (
            <Typography
              sx={{
                fontFamily: typography.fontFamilyMonospace,
                fontSize: typography.fontSizeSm,
                color: beoflowColors.beoBlack,
                fontWeight: typography.fontWeightSemibold,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                opacity: isExpanded ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              LOGOUT
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )

  if (variant === 'temporary') {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            border: 'none',
            boxShadow: '4px 0 8px rgba(0, 0, 0, 0.2)',
            maxHeight: '100dvh',
            overflow: 'hidden',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    )
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {sidebarContent}
    </Box>
  )
}

export { COLLAPSED_WIDTH, EXPANDED_WIDTH }
